import {
  TourOfferPricing,
  TourOfferPricingDeparture,
  TourOfferPricingInput,
  TourOfferPricingLevel,
  TourOfferPricingOption,
  TourOfferPricingPrice,
} from '~/types/tours/tourOffer';

export const MAX_DISCOUNT = 0.9;
export const MAX_MARKUP = 1;
export const MAX_LUX_PLUS_DISCOUNT = 0.9;

export const EMPTY_LEVEL: TourOfferPricingLevel = { discount: null, markup: null, luxPlusDiscount: null };

export type LevelSource = 'room' | 'departure' | 'option' | 'tour' | 'none';

export const LEVEL_SOURCE_LABELS: Record<LevelSource, string> = {
  room: 'This room',
  departure: 'This departure',
  option: 'The option',
  tour: 'The whole tour',
  none: 'No adjustment',
};

/** Fractions are stored (0.2); people type whole percentages (20). */
export const toPercent = (fraction: number | null): string =>
  fraction === null ? '' : String(Math.round(fraction * 10000) / 100);
export const fromPercent = (percent: string): number | null =>
  percent.trim() === '' ? null : Math.round(Number(percent) * 100) / 10000;

export const levelOf = ({ discount, markup, luxPlusDiscount }: TourOfferPricingLevel): TourOfferPricingLevel => ({
  discount,
  markup,
  luxPlusDiscount,
});

const hasAdjustment = (level: TourOfferPricingLevel) => level.discount !== null || level.markup !== null;

/** Which level's discount or markup a room price takes, most specific first, as svc-tour resolves it. */
export function adjustmentSource(
  pricing: TourOfferPricingLevel,
  option: TourOfferPricingLevel,
  departure: TourOfferPricingLevel,
  price: TourOfferPricingLevel,
): { source: LevelSource; level: TourOfferPricingLevel } {
  const candidates: Array<[LevelSource, TourOfferPricingLevel]> = [
    ['room', price],
    ['departure', departure],
    ['option', option],
    ['tour', pricing],
  ];
  const found = candidates.find(([, level]) => hasAdjustment(level));
  return found ? { source: found[0], level: found[1] } : { source: 'none', level: EMPTY_LEVEL };
}

/** Which level's LUX+ discount a room price takes. A LUX+ price of its own beats them all. */
export function luxPlusSource(
  pricing: TourOfferPricingLevel,
  option: TourOfferPricingLevel,
  departure: TourOfferPricingLevel,
  price: TourOfferPricingPrice,
): LevelSource {
  if (price.luxPlusExactPrice !== null || price.luxPlusDiscount !== null) {
    return 'room';
  }
  if (departure.luxPlusDiscount !== null) {
    return 'departure';
  }
  if (option.luxPlusDiscount !== null) {
    return 'option';
  }
  return pricing.luxPlusDiscount !== null ? 'tour' : 'none';
}

export function levelProblems(level: TourOfferPricingLevel): Array<string> {
  const problems: Array<string> = [];
  if (level.discount !== null && level.markup !== null) {
    problems.push('Set a discount or a markup, not both');
  }
  if (level.discount !== null && (level.discount < 0 || level.discount > MAX_DISCOUNT)) {
    problems.push(`Discount must be 0 to ${MAX_DISCOUNT * 100}%`);
  }
  if (level.markup !== null && (level.markup < 0 || level.markup > MAX_MARKUP)) {
    problems.push(`Markup must be 0 to ${MAX_MARKUP * 100}%`);
  }
  if (level.luxPlusDiscount !== null && (level.luxPlusDiscount < 0 || level.luxPlusDiscount > MAX_LUX_PLUS_DISCOUNT)) {
    problems.push(`LUX+ discount must be 0 to ${MAX_LUX_PLUS_DISCOUNT * 100}%`);
  }
  return problems;
}

export function priceProblems(price: TourOfferPricingPrice): Array<string> {
  const problems = levelProblems(price);
  if (price.luxPlusExactPrice !== null) {
    if (price.luxPlusDiscount !== null) {
      problems.push('Set a LUX+ discount or a LUX+ price, not both');
    }
    if (price.luxPlusExactPrice <= 0) {
      problems.push('The LUX+ price must be above 0');
    }
    if (price.luxPlusExactPrice > price.displayPrice) {
      problems.push(`The LUX+ price can't be above the display price (${price.displayPrice})`);
    }
  }
  return problems;
}

export function pricingInput(pricing: TourOfferPricing): TourOfferPricingInput {
  return {
    ...levelOf(pricing),
    options: pricing.options.map((option) => ({
      tourOptionId: option.tourOptionId,
      ...levelOf(option),
      departures: option.departures.map((departure) => ({
        departureId: departure.departureId,
        ...levelOf(departure),
        prices: departure.prices.map((price) => ({
          priceId: price.priceId,
          ...levelOf(price),
          luxPlusExactPrice: price.luxPlusExactPrice,
        })),
      })),
    })),
  };
}

export const isSameLevel = (a: TourOfferPricingLevel, b: TourOfferPricingLevel) =>
  a.discount === b.discount && a.markup === b.markup && a.luxPlusDiscount === b.luxPlusDiscount;

export interface PricingRow {
  option: TourOfferPricingOption;
  departure: TourOfferPricingDeparture;
}

export const hasOwnPricing = (departure: TourOfferPricingDeparture) =>
  hasAdjustment(departure) ||
  departure.luxPlusDiscount !== null ||
  departure.prices.some(
    (price) => hasAdjustment(price) || price.luxPlusDiscount !== null || price.luxPlusExactPrice !== null,
  );

/** A room's own discount, markup or LUX+ discount, as the Dynamic Pricing V2 table edits them. */
export type RoomField = 'discount' | 'markup' | 'luxPlusDiscount';

/** Setting a discount clears the markup and the other way round, as svc-tour keeps only one. */
export function withRoomField(
  price: TourOfferPricingPrice,
  field: RoomField,
  value: number | null,
): TourOfferPricingPrice {
  if (field === 'discount') {
    return { ...price, discount: value, markup: value === null ? price.markup : null };
  }
  if (field === 'markup') {
    return { ...price, markup: value, discount: value === null ? price.discount : null };
  }
  // A LUX+ discount replaces the room's own LUX+ price.
  return { ...price, luxPlusDiscount: value, luxPlusExactPrice: value === null ? price.luxPlusExactPrice : null };
}

export type RoomBulk = Partial<Record<RoomField, number>>;

/** V2's bulk update: each value entered is set on that room type in every given departure; blanks leave them. */
export function applyRoomBulk(
  pricing: TourOfferPricing,
  departureIds: Set<string>,
  bulk: Record<string, RoomBulk>,
): TourOfferPricing {
  return {
    ...pricing,
    options: pricing.options.map((option) => ({
      ...option,
      departures: option.departures.map((departure) =>
        departureIds.has(departure.departureId)
          ? {
              ...departure,
              prices: departure.prices.map((price) =>
                (Object.entries(bulk[price.roomType] ?? {}) as Array<[RoomField, number]>).reduce(
                  (next, [field, value]) => withRoomField(next, field, value),
                  price,
                ),
              ),
            }
          : departure,
      ),
    })),
  };
}

/** Twin first, then single, then the rest, as V2's columns run. */
export function roomTypeOrder(roomTypes: Array<string>): Array<string> {
  const rank = (roomType: string) => (roomType.startsWith('twin') ? 0 : roomType.startsWith('single') ? 1 : 2);
  return [...roomTypes].sort((a, b) => rank(a) - rank(b) || a.localeCompare(b));
}

const samePrice = (a: TourOfferPricingPrice, b: TourOfferPricingPrice | undefined) =>
  !!b && isSameLevel(a, b) && a.luxPlusExactPrice === b.luxPlusExactPrice;

/**
 * Only what changed: the tour's level, every option's level, and just the departures and prices edited.
 * svc-tour leaves the rest as they are, so old data it would now reject isn't sent back.
 */
export function changedPricingInput(draft: TourOfferPricing, saved: TourOfferPricing): TourOfferPricingInput {
  return {
    ...levelOf(draft),
    options: draft.options.map((option) => {
      const savedOption = saved.options.find((other) => other.tourOptionId === option.tourOptionId);
      return {
        tourOptionId: option.tourOptionId,
        ...levelOf(option),
        departures: option.departures.flatMap((departure) => {
          const savedDeparture = savedOption?.departures.find((other) => other.departureId === departure.departureId);
          const prices = departure.prices.filter(
            (price) =>
              !samePrice(
                price,
                savedDeparture?.prices.find((other) => other.priceId === price.priceId),
              ),
          );
          if (savedDeparture && isSameLevel(departure, savedDeparture) && prices.length === 0) {
            return [];
          }
          return [
            {
              departureId: departure.departureId,
              ...levelOf(departure),
              prices: prices.map((price) => ({
                priceId: price.priceId,
                ...levelOf(price),
                luxPlusExactPrice: price.luxPlusExactPrice,
              })),
            },
          ];
        }),
      };
    }),
  };
}

/** Problems in what will be saved. Untouched departures aren't sent, so they aren't checked. */
export function changedProblemCount(draft: TourOfferPricing, saved: TourOfferPricing): number {
  const input = changedPricingInput(draft, saved);
  const sentPrices = new Set(
    input.options.flatMap((option) =>
      option.departures.flatMap((departure) => departure.prices.map((price) => price.priceId)),
    ),
  );
  const sentDepartures = new Set(
    input.options.flatMap((option) => option.departures.map((departure) => departure.departureId)),
  );
  return (
    levelProblems(draft).length +
    draft.options.reduce(
      (total, option) =>
        total +
        levelProblems(option).length +
        option.departures.reduce(
          (sum, departure) =>
            sum +
            (sentDepartures.has(departure.departureId) ? levelProblems(departure).length : 0) +
            departure.prices
              .filter((price) => sentPrices.has(price.priceId))
              .reduce((count, price) => count + priceProblems(price).length, 0),
          0,
        ),
      0,
    )
  );
}
